import React, { useState } from "react";

import {
  Typography,
  Button,
  Grid,
  TextField,
  Card,
  Table,
  TableCell,
  TableHead,
  TableRow,
  TableBody,
  TableContainer,
  Chip,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from "@mui/material";
import { getJobOrders } from "../API/jobOrderAPI";
import { startServiceReport } from "../API/serviceReportAPI";
import { useNavigate } from "react-router-dom";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import { BaseFormWrapper } from "../../components/shared/BaseFormWrapper";
import { StyledHr } from "../../components/shared/CustomDivider";
import { fetchAndFilterJOsBySingleDate } from "../JobOrders/utils/JobOrderFilter";
import { formatTo12Hour, formatFullDateTime } from "./utils/timeFormatter";

const getCurrentWorkdayDateStr = () => {
  const now = new Date();

  // If before 05:00, treat as previous date for "workday"
  const workday = new Date(now);
  if (now.getHours() < 5) {
    workday.setDate(workday.getDate() - 1);
  }

  // Format as YYYY-MM-DD in LOCAL time (avoid toISOString() timezone shifts)
  const yyyy = workday.getFullYear();
  const mm = String(workday.getMonth() + 1).padStart(2, "0");
  const dd = String(workday.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

function CreateServiceReport() {
  const [jobOrders, setJobOrders] = useState([]);
  const [dateFilter, setDateFilter] = useState();
  const [totalJobOrders, setTotalJobOrders] = useState(null);
  // get local user data to be used for tech view filter
  const storedUser = localStorage.getItem("user");
  const parsedUser = storedUser ? JSON.parse(storedUser) : {};
  const userRoles     = parsedUser.roles      || [];
  const technicianId = parsedUser.technician_id || "";
  // –––

  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [selectedJobOrder, setSelectedJobOrder] = useState(null);
  const [startingJobId, setStartingJobId] = useState("");

  const handleSearchButtonClick = async () => {
    try {
      setIsLoading(true);
      setErrorMessage("");

      if (!dateFilter) {
        setJobOrders([]);
        setTotalJobOrders(0);
        return;
      }

      // --- FUTURE DATE RESTRICTION FOR TECHNICIANS ---
      const isTechnician = userRoles.includes("operations_technician"); // keep it strict

      if (isTechnician) {
        const currentWorkday = getCurrentWorkdayDateStr();

        // dateFilter is already "YYYY-MM-DD" so lexicographic compare works
        if (dateFilter > currentWorkday) {
          setJobOrders([]);
          setTotalJobOrders(0);
          setErrorMessage(
            "You are restricted from viewing future Job Orders. Please contact your administrator for access."
          );
          return;
        }
      }
      // --- END RESTRICTION ---

      const { list, total } = await fetchAndFilterJOsBySingleDate({
        date: dateFilter,
        userRoles,
        technicianId,
        getJobOrdersApi: getJobOrders,
        serviceReportStatus: ["Not Done"],
      });

      // console.log("Fetched job orders:", list);

      // list.forEach((job, i) => {
      //   if (!job?.client?.contact?.numbers) {
      //     console.warn("Missing numbers", i, job?.id, job);
      //   }
      //   if (!job?.client?.contact?.emails) {
      //     console.warn("Missing emails", i, job?.id, job);
      //   }
      //   if (!job?.client?.contact) {
      //     console.warn("Missing contact", i, job?.id, job);
      //   }
      // });

      setJobOrders(list);
      setTotalJobOrders(total);
    } catch (e) {
      setErrorMessage("Failed to fetch Service Reports. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleStartClick = (jobOrder) => {
    setErrorMessage("");
    setSelectedJobOrder(jobOrder);
    setConfirmDialogOpen(true);
  };

  const handleCreateReport = (jobOrder) => {
    const { id, form_type } = jobOrder;

    if (!form_type) {
      setErrorMessage("Please select a form type before proceeding.");
      return;
    }

    setErrorMessage("");

    let route;
    switch (form_type) {
      case "makati_gpc_form":
        route = `/makati-gpc-form/${id}`;
        break;
      case "qc_form":
        route = `/qc-form/${id}`;
        break;
      case "termite_control_form":
        route = `/termite-control-form/${id}`;
        break;
      case "transmittal_form":
        route = `/transmittal-form/${id}`;
        break;
      default:
        setErrorMessage(`Unknown form type: ${form_type}`);
        return;
    }

    navigate(route);
  };

  const handleConfirmStart = async () => {
    if (!selectedJobOrder) return;

    try {
      setIsLoading(true);
      setErrorMessage("");
      setStartingJobId(selectedJobOrder.id);

      const storedUser = localStorage.getItem("user");
      const parsedUser = storedUser ? JSON.parse(storedUser) : {};
      const user_update = parsedUser.username || "";

      const result = await startServiceReport(selectedJobOrder.id, {
        user_update,
      });

      const updatedJobOrder = result?.job_order;

      if (!updatedJobOrder) {
        throw new Error("No updated job order returned from server");
      }

      setJobOrders((prev) =>
        prev.map((job) =>
          job.id === selectedJobOrder.id ? updatedJobOrder : job
        )
      );

      setConfirmDialogOpen(false);
      setSelectedJobOrder(null);
    } catch (error) {
      setErrorMessage(
        `Failed to start job order: ${error.message || "Unknown error"}`
      );
    } finally {
      setIsLoading(false);
      setStartingJobId("");
    }
  };

  const handleCloseConfirmDialog = () => {
    if (isLoading) return;
    setConfirmDialogOpen(false);
    setSelectedJobOrder(null);
  };

  const hasJobStarted = (jobOrder) => {
    return Boolean(jobOrder.report_started);
  };

  return (
    <BaseFormWrapper
      titleIcon={<ReceiptLongIcon sx={{ fontSize: "40px", marginRight: "8px" }} />}
      title="Operations"
      subTitle="Service Reports"
      subTitleRoute="/manage-service-reports"
      formTitle="Create Service Report"
      errorMessage={errorMessage}
      setErrorMessage={setErrorMessage}
      isLoading={isLoading}
    >
      <Grid container spacing={3} alignItems="center">
        <Grid item xs={12}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <TextField
                label="Date"
                name="date_filter"
                type="date"
                value={dateFilter || ""}
                onChange={(e) => setDateFilter(e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={4} container alignItems="center">
              <Button
                variant="contained"
                color="primary"
                onClick={handleSearchButtonClick} // Call button handler
                disabled={isLoading}
              >
                {isLoading ? "Searching..." : "Search"}{" "}
              </Button>
            </Grid>

            <Grid item xs={12}>
              <StyledHr />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      {jobOrders.length > 0 ? (
        <>
          <Grid item xs={12} container spacing={1} sx={{ marginBottom: 1 }}>
            <Grid item xs={12} sm={6} md={3} lg={2}>
              <Card
                sx={{
                  backgroundColor: '#1976D2',
                  color: '#FFF',
                  borderRadius: 1,
                  textAlign: 'center',
                  padding: '4px',
                  boxShadow: 1
                }}
              >
                <Box sx={{ padding: '4px' }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}>
                    Total Job Orders
                  </Typography>
                  <Typography variant="h5" sx={{ fontSize: '1.5rem' }}>
                    {totalJobOrders}
                  </Typography>
                </Box>
              </Card>
            </Grid>
          </Grid>

          <Card variant="outlined" sx={{ padding: "20px" }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <b>Client</b>
                    </TableCell>
                    <TableCell>
                      <b>Address</b>
                    </TableCell>
                    <TableCell>
                      <b>Contact</b>
                    </TableCell>
                    <TableCell>
                      <b>Number</b>
                    </TableCell>
                    <TableCell>
                      <b>Email</b>
                    </TableCell>
                    <TableCell>
                      <b>Schedule</b>
                    </TableCell>
                    <TableCell>
                      <b>Job Type</b>
                    </TableCell>
                    <TableCell>
                      <b>Technicians</b>
                    </TableCell>
                    <TableCell>
                      <b>Action</b>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {jobOrders.map((jobOrder, index) => (
                    <TableRow key={index}>
                      <TableCell>{jobOrder?.client?.name || "N/A"}</TableCell>
                      <TableCell>{[
                        jobOrder?.client?.address?.house_unit_street,
                        jobOrder?.client?.address?.city,
                        jobOrder?.client?.address?.metro_province,
                      ]
                        .filter(Boolean)
                        .join(", ") || "N/A"}</TableCell>
                      <TableCell>{[
                        jobOrder?.client?.contact?.firstname,
                        jobOrder?.client?.contact?.lastname,
                      ]
                        .filter(Boolean)
                        .join(" ") || "N/A"}</TableCell>
                      <TableCell>
                        {jobOrder?.client?.contact?.numbers?.[0]?.number || "N/A"}
                      </TableCell>
                      <TableCell>
                        {jobOrder?.client?.contact?.emails?.[0]?.email || "N/A"}
                      </TableCell>
                      <TableCell>
                        <div>
                          {formatTo12Hour(jobOrder.scheduled_start_time || jobOrder.start_time)} -{" "}
                          {formatTo12Hour(jobOrder.end_time)}
                        </div>
                      </TableCell>                      
                      <TableCell>
                        {Array.isArray(jobOrder.job_types) && jobOrder.job_types.length > 0 ? (
                          <Box
                            sx={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 1,
                            }}
                          >
                            {jobOrder.job_types.map((type, i) => (
                              <Chip
                                key={i}
                                label={type.replace(/_/g, " ")}
                                variant="outlined"
                                size="small"
                              />
                            ))}
                          </Box>
                        ) : (
                          "No job types available"
                        )}
                      </TableCell>
                      <TableCell>
                        {jobOrder.technicians?.length > 0
                          ? jobOrder.technicians.map((technician, i) => (
                              <div
                                key={i}
                              >{`${technician.firstname} ${technician.lastname}`}</div>
                            ))
                          : "No technicians assigned"}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                          <Button
                            variant="contained"
                            color="primary"
                            onClick={() =>
                              hasJobStarted(jobOrder)
                                ? handleCreateReport(jobOrder)
                                : handleStartClick(jobOrder)
                            }
                            disabled={isLoading && startingJobId === jobOrder.id}
                          >
                            {isLoading && startingJobId === jobOrder.id
                              ? "Starting..."
                              : hasJobStarted(jobOrder)
                              ? "Create Report"
                              : "Start"}
                          </Button>

                          {hasJobStarted(jobOrder) && (
                            <Typography variant="caption" color="success.main">
                              Job started at {formatFullDateTime(jobOrder.report_started)}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </>
      ) : (
        <Grid
          container
          alignItems="center"
          justifyContent="center"
          paddingBottom={1}
        >
          <Typography variant="body1">No service reports available.</Typography>
        </Grid>
      )}

      <Dialog
        open={confirmDialogOpen}
        onClose={handleCloseConfirmDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Start Job Order</DialogTitle>
        <DialogContent>
          <Typography>
            Do you want to start this job? Please click YES if you have arrived on site.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmDialog} disabled={isLoading}>
            No
          </Button>
          <Button
            onClick={handleConfirmStart}
            variant="contained"
            color="primary"
            disabled={isLoading}
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </BaseFormWrapper>
  );
}

export default CreateServiceReport;
